import React from 'react';
import {Route, Routes} from 'react-router-dom';
import {
    AuditOutlined,
    CommentOutlined,
    MessageOutlined,
    PicLeftOutlined,
    PictureOutlined,
    PieChartOutlined,
    RollbackOutlined,
    SendOutlined,
    SettingOutlined,
    TagsOutlined,
} from '@/icons';
import {useAppStore} from '@/page/app/context';

const PARENT_PREFIX = '/apps/:appId';

// 带子路由的父级：渲染时必须追加 /*，否则 /apps/:appId/media/news、
// /apps/:appId/report/user_increase 等子 URL 会因 v6 精确匹配而 404
const NESTED_PARENT_PATHS = ['/apps/:appId/media', '/apps/:appId/report'];

// 父级 <Route path="/apps/:appId/*"> 已经消耗了 /apps/:appId，
// 子级 <Routes> 必须用相对路径。菜单配置里保持绝对路径便于 navigate/matchPath。
const toRelative = (absPath) => {
    if (absPath === PARENT_PREFIX) {
        return '';
    }
    if (absPath === '*') {
        return '*';
    }
    if (absPath.startsWith(PARENT_PREFIX + '/')) {
        return absPath.slice(PARENT_PREFIX.length + 1);
    }
    return absPath;
};

const AppRouteConfig = [
    {
        path: '/apps/:appId',
        exact: true,
        component: require('./MsgSendPage').default,
    },
    {
        label: '发送消息',
        path: '/apps/:appId/msg-send',
        exact: true,
        icon: <SendOutlined />,
        component: require('./MsgSendPage').default,
    },
    {
        path: '/apps/:appId/msg-send/list',
        component: require('./msg-audit/MyAuditMsgList').default,
    },
    {
        label: '待审核消息',
        path: '/apps/:appId/audit-msg',
        icon: <AuditOutlined />,
        isAuditMgr: true,     // 只有审核人才能查看此条目
        component: require('./msg-audit/MgrAuditMsgList').default,
    },
    {
        label: '历史消息',
        key: 'msg-history',
        icon: <MessageOutlined />,
        children: [
            {
                label: '已发送消息',
                path: '/apps/:appId/msg-history-send',
                component: require('./msg-history/MsgHistorySendPage').default,
            },
            {
                label: '已接收消息',
                path: '/apps/:appId/msg-history-received',
                component: require('./msg-history/MsgHistoryReceivedPage').default,
            },
        ],
    },
    {
        label: '留言管理',
        key: 'comment',
        icon: <CommentOutlined />,
        path: '/apps/:appId/comments',
        component: require('./CommentPage').default,
    },
    {
        label: '素材管理',
        path: '/apps/:appId/media',
        icon: <PictureOutlined />,
        component: require('./MediaPage').default,
    },
    {
        label: '数据统计',
        path: '/apps/:appId/report',
        icon: <PieChartOutlined />,
        component: require('./ReportPage').default,
    },
    {
        label: '可用标签',
        path: '/apps/:appId/tag',
        icon: <TagsOutlined />,
        component: require('./AppTagsPage').default,
    },
    {
        label: '自动回复',
        path: '/apps/:appId/reply',
        icon: <RollbackOutlined />,
        component: require('./MsgAutoReply').default,
    },
    {
        label: '菜单管理',
        path: '/apps/:appId/menu',
        icon: <PicLeftOutlined />,
        isAppMgr: true,
        component: require('./AppMenusPage').default,
    },
    {
        label: '应用设置',
        path: '/apps/:appId/settings',
        icon: <SettingOutlined />,
        isAppMgr: true,
        component: require('./AppSettingsPage').default,
    },
    {
        path: '/apps/:appId/*',
        component: require('../NotFoundPage').default,
    },
];

export const MenuConfig = AppRouteConfig.filter(item => !!item.label);

export default function AppRoutes() {
    const [{isMgr, extAttr}] = useAppStore();
    const auditMgr = extAttr?.userAttr?.auditMgr ?? false;

    const child = [];
    AppRouteConfig.forEach(({children}) => {
        if (children && children.length > 0) {
            child.push(...children);
        }
    });

    return (
        <Routes>
            {child.map(({path, isAppMgr, isAuditMgr, component: Component}) => {
                if (isAppMgr && !isMgr) {
                    return null;
                }

                if (isAuditMgr && !auditMgr) {
                    return null;
                }

                return (
                    <Route
                        key={`subroute${path}`}
                        path={toRelative(path)}
                        element={<Component />}
                    />
                );
            })}

            {AppRouteConfig.map(({path, isAppMgr, isAuditMgr, component: Component}) => {
                if (Component && path) {
                    if (isAppMgr && !isMgr) {
                        return null;
                    }

                    if (isAuditMgr && !auditMgr) {
                        return null;
                    }

                    return (
                        <Route
                            key={`route${path}`}
                            path={toRelative(path) + (NESTED_PARENT_PATHS.includes(path) ? '/*' : '')}
                            element={<Component />}
                        />
                    );
                }

                return null;
            })}
        </Routes>
    );
}
